// components/market/ImagePickerField.tsx
//
// Champ de sélection + upload d'image, réutilisable pour la couverture
// d'une boutique ou d'un produit. Reprend le même pattern que le reste
// de l'app (expo-image-picker, déjà utilisé pour l'avatar de profil) :
//   1. launchImageLibraryAsync pour choisir une image locale
//   2. useMarketUpload pour l'uploader vers Supabase Storage (signed URL)
//   3. onUploaded(publicUrl) informe le parent une fois l'upload terminé,
//      pour qu'il l'enregistre (create-shop suit un update, ou
//      create-product suit un update-product avec cover_image_url)
//
// PATCH i18n : les textes affichés (overlay Changer/Ajouter une image,
// messages d'erreur) passent par react-i18next (namespace
// market.components.imagePicker).

import React, { useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Image, ActivityIndicator, Platform } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { useTranslation } from 'react-i18next';

import { useMarketUpload } from '@/hooks/market/useMarketUpload';

const NATIVE = Platform.OS !== 'web';

type ImagePickerFieldProps = {
  context: 'shop-cover' | 'product-cover';
  ids: { shop_id: string; product_id?: string };
  currentImageUrl?: string | null;
  onUploaded: (publicUrl: string) => void;
};

export default function ImagePickerField({ context, ids, currentImageUrl, onUploaded }: ImagePickerFieldProps) {
  const { t } = useTranslation();
  const { uploadImage, uploading, error } = useMarketUpload();

  const handlePickImage = useCallback(async () => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);

    if (Platform.OS !== 'web') {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [4, 3],
      quality: 0.8,
    });

    if (result.canceled || !result.assets[0]) return;

    const publicUrl = await uploadImage(context, result.assets[0].uri, ids);
    if (publicUrl) {
      onUploaded(publicUrl);
    }
  }, [context, ids, uploadImage, onUploaded]);

  return (
    <TouchableOpacity style={styles.container} activeOpacity={0.8} onPress={handlePickImage} disabled={uploading}>
      {currentImageUrl ? (
        <Image source={{ uri: currentImageUrl }} style={styles.image} resizeMode="cover" />
      ) : (
        <View style={styles.placeholder}>
          <Ionicons name="image-outline" size={28} color="#C4B5FD" />
        </View>
      )}

      <View style={styles.overlay}>
        {uploading ? (
          <ActivityIndicator size="small" color="#fff" />
        ) : (
          <>
            <Ionicons name="camera-outline" size={16} color="#fff" />
            <Text style={styles.overlayText}>
              {currentImageUrl ? t('market.components.imagePicker.change') : t('market.components.imagePicker.add')}
            </Text>
          </>
        )}
      </View>

      {!!error && (
        <Text style={styles.errorText}>
          {error === 'session_expired' ? t('market.components.imagePicker.sessionExpired') : t('market.components.imagePicker.uploadError')}
        </Text>
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: 140,
    borderRadius: 14,
    backgroundColor: '#F3E8FF',
    overflow: 'hidden',
    position: 'relative',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  placeholder: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  overlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 8,
    backgroundColor: 'rgba(0,0,0,0.45)',
  },
  overlayText: {
    fontSize: 12.5,
    fontWeight: '600',
    color: '#fff',
  },
  errorText: {
    position: 'absolute',
    top: 8,
    left: 8,
    right: 8,
    fontSize: 11,
    color: '#fff',
    backgroundColor: 'rgba(220,38,38,0.85)',
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 2,
    textAlign: 'center',
  },
});
